const FUNCTIONS_URL =
  import.meta.env.VITE_PDF_STRATEGY_FUNCTION_URL ||
  import.meta.env.VITE_PDF_FUNCTION_URL ||
  'https://procesarestrategiacompleta-g7gaximslq-uc.a.run.app';

const DOCAI_UNIVERSAL_URL =
  import.meta.env.VITE_PDF_DOCAI_FUNCTION_URL ||
  'https://procesaransespdf-g7gaximslq-uc.a.run.app';

function buildUserMessage(status, errorBody) {
  if (status >= 500) {
    return 'El servicio de conversión está temporalmente saturado o fuera de línea. Por favor, intentá de nuevo en unos minutos.';
  }
  if (status === 404) {
    return 'El servicio de conversión no está disponible en este momento (endpoint no encontrado).';
  }
  if (status === 413) {
    return 'El archivo PDF es demasiado grande para ser procesado por el servicio de conversión.';
  }
  if (status === 429) {
    return 'Se realizaron demasiadas solicitudes en poco tiempo. Por favor, esperá unos segundos y volvé a intentar.';
  }
  if (errorBody && errorBody.error) {
    return errorBody.error;
  }
  return 'Error inesperado al procesar el PDF (código ' + status + ').';
}

function toPeriodoKey(value) {
  const txt = String(value || '').trim();
  const slash = txt.match(/^(\d{2})\/(\d{4})$/);
  if (slash) return `${slash[2]}-${slash[1]}`;
  const iso = txt.match(/^(\d{4})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}`;
  return '';
}

function computeLastPeriod(remuneraciones) {
  const keys = (Array.isArray(remuneraciones) ? remuneraciones : [])
    .map((r) => toPeriodoKey(r.periodo || r.periodoISO || `${r.ano || ''}-${String(r.mes || '').padStart(2, '0')}`))
    .filter(Boolean)
    .sort();
  return keys.length ? keys[keys.length - 1] : null;
}

function normalizeCuit(value) {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length === 11 ? digits : '';
}

function parseMoneyToken(token) {
  const raw = String(token || '').trim();
  if (!raw) return 0;
  const cleaned = raw.replace(/\$/g, '').replace(/\s/g, '');
  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');
  let normalized = cleaned;
  if (hasComma && hasDot) {
    normalized = cleaned.replace(/\./g, '').replace(',', '.');
  } else if (hasComma) {
    normalized = cleaned.replace(',', '.');
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

function collectSemanticSummaryTexts(data) {
  const chunks = [];
  const push = (value) => {
    if (!value) return;
    if (typeof value === 'string') {
      chunks.push(value);
      return;
    }
    try {
      chunks.push(JSON.stringify(value));
    } catch {
      // ignore
    }
  };

  push(data?.resumenHistoriaLaboral);
  push(data?.resumen_historia_laboral);
  push(data?.markdownTableConsolidada);
  push(data?.markdownTable);
  push(data?.etlSummary);
  push(data?.debug?.resumenHistoriaLaboral);
  push(data?.debug?.resumen_historia_laboral);
  push(data?.debug?.firstPageText);
  push(data?.debug?.page1Text);
  push(data?.debug?.ocrText);

  return chunks.join('\n');
}

function extractTotalsBySemanticProximity(data) {
  const text = collectSemanticSummaryTexts(data);
  if (!text) return { totalesPorCuit: {}, source: 'none', evidencias: [] };

  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const cuitRegex = /(\b\d{2}[\s.-]?\d{8}[\s.-]?\d\b|\b\d{11}\b)/g;
  const moneyRegex = /\$?\s*\d{1,3}(?:[.,]\d{3})*(?:[.,]\d{1,2})|\$?\s*\d{4,}/g;
  const keywordRegex = /(total|remuneraciones|resumen|acumulado|importe)/i;

  const totalsMap = new Map();
  const evidencias = [];

  for (let i = 0; i < lines.length; i += 1) {
    const base = lines[i];
    const cuitMatches = Array.from(base.matchAll(cuitRegex)).map((m) => normalizeCuit(m[0])).filter(Boolean);
    if (!cuitMatches.length) continue;

    const context = [base, lines[i + 1] || '', lines[i + 2] || '', lines[i + 3] || ''].join(' ');
    const moneyMatches = Array.from(context.matchAll(moneyRegex))
      .map((m) => parseMoneyToken(m[0]))
      .filter((n) => n > 0);
    if (!moneyMatches.length) continue;

    const candidateTotal = Math.max(...moneyMatches);
    cuitMatches.forEach((cuit) => {
      const prev = totalsMap.get(cuit) || 0;
      if (candidateTotal > prev) totalsMap.set(cuit, candidateTotal);
      evidencias.push({ cuit, line: base, totalDetectado: candidateTotal });
    });
  }

  // Estrategia 2: ventana semantica por bloques con palabras clave.
  for (let i = 0; i < lines.length; i += 1) {
    const block = [lines[i - 1] || '', lines[i] || '', lines[i + 1] || '', lines[i + 2] || '', lines[i + 3] || ''].join(' ');
    if (!keywordRegex.test(block)) continue;
    const cuits = Array.from(block.matchAll(cuitRegex)).map((m) => normalizeCuit(m[0])).filter(Boolean);
    if (!cuits.length) continue;
    const amounts = Array.from(block.matchAll(moneyRegex)).map((m) => parseMoneyToken(m[0])).filter((n) => n > 0);
    if (!amounts.length) continue;
    const strongest = Math.max(...amounts);
    cuits.forEach((cuit) => {
      const prev = totalsMap.get(cuit) || 0;
      if (strongest > prev) totalsMap.set(cuit, strongest);
      evidencias.push({ cuit, line: lines[i], totalDetectado: strongest, metodo: 'keyword_window' });
    });
  }

  // Estrategia 3: parser de pares CUIT->monto en linea corrida (OCR sin saltos)
  const compact = lines.join(' | ');
  const pairRegex = /(\b\d{2}[\s.-]?\d{8}[\s.-]?\d\b|\b\d{11}\b)([^\d$]{0,25})(\$?\s*\d{1,3}(?:[.,]\d{3})*(?:[.,]\d{1,2})|\$?\s*\d{4,})/g;
  for (const match of compact.matchAll(pairRegex)) {
    const cuit = normalizeCuit(match[1]);
    const amount = parseMoneyToken(match[3]);
    if (!cuit || !(amount > 0)) continue;
    const prev = totalsMap.get(cuit) || 0;
    if (amount > prev) totalsMap.set(cuit, amount);
    evidencias.push({ cuit, line: match[0], totalDetectado: amount, metodo: 'inline_pair' });
  }

  const out = {};
  totalsMap.forEach((v, k) => { out[k] = Number(v.toFixed(2)); });

  const amountFreq = new Map();
  Object.values(out).forEach((n) => {
    const key = Number(n).toFixed(2);
    amountFreq.set(key, (amountFreq.get(key) || 0) + 1);
  });
  const maxDup = amountFreq.size ? Math.max(...Array.from(amountFreq.values())) : 0;
  const duplicateRatio = Object.keys(out).length ? maxDup / Object.keys(out).length : 0;
  let sourceConfidence = 0;
  if (Object.keys(out).length) sourceConfidence = 0.85;
  if (Object.keys(out).length >= 3 && duplicateRatio > 0.5) {
    sourceConfidence = 0.35;
  }

  return {
    totalesPorCuit: out,
    source: totalsMap.size > 0 ? 'semantic_proximity_page1' : 'none',
    evidencias,
    sourceConfidence,
    duplicateRatio,
  };
}

export function enrichResumenFromSemantic(parserData = {}) {
  const semanticSummary = extractTotalsBySemanticProximity(parserData);
  const incomingTotals = parserData.totalesPorCuit || parserData.totales_por_cuit || null;
  const canUseSemantic = semanticSummary.source !== 'none' && Number(semanticSummary.sourceConfidence || 0) >= 0.75;
  const totalesPorCuit = incomingTotals || (canUseSemantic ? semanticSummary.totalesPorCuit : null);
  const resumenHistoriaLaboral = parserData.resumenHistoriaLaboral || parserData.resumen_historia_laboral || (semanticSummary.source !== 'none' ? {
    source: semanticSummary.source,
    totalesPorCuit: canUseSemantic ? semanticSummary.totalesPorCuit : null,
    evidencias: semanticSummary.evidencias,
    sourceConfidence: semanticSummary.sourceConfidence,
    duplicateRatio: semanticSummary.duplicateRatio,
    qualityWarning: canUseSemantic ? '' : 'Resumen detectado pero no confiable por duplicacion o baja consistencia',
  } : null);
  return {
    ...parserData,
    totalesPorCuit,
    resumenHistoriaLaboral,
  };
}

export async function parsePdfWithCloudmersive(pdfArrayBuffer, options = {}) {
  try {
    const blob = new Blob([pdfArrayBuffer], { type: 'application/pdf' });
    const formData = new FormData();
    formData.append('file', blob, 'document.pdf');

    const targetUrl = options.profile === 'docai_universal' ? DOCAI_UNIVERSAL_URL : FUNCTIONS_URL;
    const token =
      options.idToken ||
      options.userToken ||
      localStorage.getItem('userToken') ||
      localStorage.getItem('adminToken') ||
      '';
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    const response = await fetch(targetUrl, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      let errorBody = null;
      try {
        errorBody = await response.json();
      } catch {
        try {
          errorBody = { error: await response.text() };
        } catch {
          errorBody = {};
        }
      }

      const userMessage = buildUserMessage(response.status, errorBody);
      const enriched = new Error(userMessage);
      enriched.status = response.status;
      enriched.originalError = errorBody && errorBody.error
        ? errorBody.error
        : ('HTTP ' + response.status);
      throw enriched;
    }

    const data = await response.json();

    if (!data.remuneraciones || !Array.isArray(data.remuneraciones) || data.remuneraciones.length === 0) {
      throw Object.assign(
        new Error('Error: No se recibió tabla estructurada de la Cloud Function'),
        { status: 0, originalError: 'Respuesta vacía del servidor' }
      );
    }

    const enriched = enrichResumenFromSemantic(data);

    return {
      remuneraciones: data.remuneraciones,
      ultimoPeriodoDetectado: data.ultimoPeriodoDetectado || computeLastPeriod(data.remuneraciones),
      historico_autonomos: data.historico_autonomos || data.historicoAutonomos || [],
      historico_desempleo: data.historico_desempleo || data.historicoDesempleo || data.desempleo || [],
      resumenHistoriaLaboral: enriched.resumenHistoriaLaboral,
      resumenHistoriaLaboralDetalle: data.resumenHistoriaLaboralDetalle || data?.etlSummary?.resumenHistoriaLaboralDetalle || [],
      historiaLaboralHasta0694: data.historiaLaboralHasta0694 || [],
      totalesPorCuit: enriched.totalesPorCuit,
      cuil: data.cuil || null,
      alertas: data.alertas || null,
      etlSummary: data.etlSummary || null,
      escenariosEstrategia: data.escenariosEstrategia || null,
      etlSummaryConservador: data.etlSummaryConservador || null,
      etlSummaryLitigio: data.etlSummaryLitigio || null,
      markdownTable: data.markdownTable || null,
      markdownTableConsolidada: data.markdownTableConsolidada || null,
      debug: data.debug || null,
    };
  } catch (err) {
    if (err.status !== undefined) {
      throw err;
    }
    console.error('[Cloud Function Error]', err);
    const enriched = new Error('Error de conexión con el servicio de conversión. Verificá tu conexión a internet.');
    enriched.status = 0;
    enriched.originalError = err.message;
    throw enriched;
  }
}
